import {
  AfterViewChecked, AfterViewInit, ChangeDetectorRef,
  Component, ComponentFactoryResolver, ElementRef,
  EventEmitter,
  HostListener, Inject, OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren, ViewContainerRef
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { ConfigProvider } from "../../../config-provider.service";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { CurrentUser } from "../../../common/providers/current-user";
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { SessionRow, SessionsProvider } from '../../providers/sessions-provider';
import { SessionItemRow, SessionItemsProvider } from '../../providers/sessionItems-provider';
import {
  ExerciceRow,
  exerciceOrderByOptions,
  ExercicesProvider
} from '../../../exercices/providers/exercices-provider';
import { ExercicesFormComponent } from '../../../exercices/components/form/form';
import { SessionsGeneralPanelComponent } from '../../components/session-general-panel/session-general-panel';
import { HasPendingChangesGuard } from '../../../common/guards/has-pending-changes';
import { BreadcrumbProvider, Crumb } from '../../../breadcrumb/providers/breadcrumb-provider';
import { SessionRights } from '../../providers/session-rights';
import { EditorService } from '../../services/editor.service';
import { SessionBlocEditComponent } from '../../components/session-bloc/session-bloc-edit';
import { EditableBlockInterface } from '../../editable-block-interface';
import { isObject } from '@datorama/akita';
import { ErrorManagerService } from '../../../common/providers/error-manager-service';
import { forkJoin, fromEvent, Observable, of, Subject, Subscription, switchMap, takeUntil, throwError } from 'rxjs';
import { ExerciseDatasource } from '../../../common/datasource/exercise.datasource';
import { debounceTime, distinctUntilChanged, take, tap, filter, map, catchError, startWith } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Organization } from "../../../common/models/organization";
import { Library } from "../../../common/models/library";
import { ExerciceCategory } from "../../../common/models/exercice-category";
import { Session } from "../../../common/models/session";
import { Exercice } from "../../../common/models/exercice";
import { ApiBaseService } from "../../../common/providers/api-base-service";
import { SessionItem } from "../../../common/models/session-item";
import { SessionItemWrapper } from '../../../common/models/session-item-wrapper';
import { SessionItemExercice } from "../../../common/models/session-item-exercice";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { Error401Component } from "../../../common/components/error-401/error-401";
import { Error404Component } from "../../../common/components/error-404/error-404";
import { AsyncPipe, NgClass, NgForOf, NgIf } from "@angular/common";
import { ScrollTrackerDirective } from "../../../directive/ScrollTrackerDirective";
import { MatIconModule } from "@angular/material/icon";
import { FlexLayoutModule } from "@angular/flex-layout";
import { SessionRestComponent } from "../../components/session-rest/session-rest";
import { SessionCommentComponent } from "../../components/session-comment/session-comment";
import { SessionBlocComponent } from "../../components/session-bloc/session-bloc";
import { MainActionComponent } from "../../../common/components/main-action/main-action";
import { ExercicePreviewComponent } from "../../components/exercice-preview/exercice-preview";
import { MatError, MatFormField, MatHint, MatSuffix } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatTooltip } from "@angular/material/tooltip";
import { CdkDrag, CdkDragDrop, CdkDropList, DragDropModule, moveItemInArray } from "@angular/cdk/drag-drop";
import { LibraryService } from "../../../libraries/providers/library.services";
import { CenterLoaderComponent } from "../../../common/components/center-loader/center-loader";
import { SimpleMatDataSource } from "../../../common/providers/simple-mat-data-source";
import {
  ExerciceCategoryFiltersComponent
} from "../../../exercices/components/exercice-category-filters/exercice-category-filters";
import {
  ExerciceCategorySortingComponent
} from "../../../exercices/components/exercice-category-sorting/exercice-category-sorting";
import { CdkTextareaAutosize } from "@angular/cdk/text-field";
import { Paginator } from "../../../common/models/paginator";
import { LangsProvider, Lang } from "../../../common/providers/langs-provider";


@Component({
  imports: [
    PageContentComponent,
    HeaderComponent,
    Error401Component,
    Error404Component,
    NgIf,
    ScrollTrackerDirective,
    MatIconModule,
    NgClass,
    TranslateModule,
    FlexLayoutModule,
    SessionRestComponent,
    SessionCommentComponent,
    SessionBlocComponent,
    NgForOf,
    MainActionComponent,
    SessionsGeneralPanelComponent,
    MatPaginator,
    ExercicePreviewComponent,
    MatFormField,
    MatError,
    MatHint,
    FormsModule,
    ReactiveFormsModule,
    MatSuffix,
    MatInput,
    MatTooltip,
    AsyncPipe,
    DragDropModule,
    CenterLoaderComponent,
    ExerciceCategoryFiltersComponent,
    ExerciceCategorySortingComponent,
    CdkTextareaAutosize,
    MatCheckboxModule
  ],
  providers: [],
  selector: 'session-editor-page',
  standalone: true,
  styleUrls: ['./editor.scss'],
  templateUrl: './editor.html'
})

export class SessionEditorPageComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {

  private needsDropListUpdate = false;

  public get hasPendingChanges() {
    return this._changesGuard.hasChanges();
  }

  public readonly DROPZONES: Map<string, string[]> = SessionItemsProvider.DROPZONES;
  public readonly TITLE_MAX_LENGTH: number = 200;
  public readonly COMMENTS_MAX_LENGTH: number = 1000;
  public readonly TAGS_MAX_LENGTH: number = 1000;

  @ViewChild(MatPaginator, {static: false}) public paginator: MatPaginator | undefined;
  @ViewChild('searchExercise', {static: false}) public input: ElementRef = {} as ElementRef;
  @ViewChild('exercicePanel', {read: ElementRef}) private exercicePanel: ElementRef | undefined;

  public libraryContext: number = LibraryService.CONTEXT_ELITMENT;
  public pageTitle = 'SessionEditor';
  public isLoading = true;
  public sideLoading = false;
  public isRefreshLoading = false;
  public allValuesAreGood = true;
  public loadingResumedInfo = false;
  public isLeftPanelOpen = false;
  public is401 = false;
  public is404 = false;

  public exerciceDropList: string[] = [];

  public sessionId = 0;
  public organization: Organization = {} as Organization;
  public library: Library = {} as Library;
  public libraries: Library[] = [];
  public librariesIds: number[] = [];
  public rights: SessionRights = new SessionRights(this._utils);
  public sessionRow: SessionRow = {} as SessionRow;
  public sessionItems: SessionItemRow[] = [];
  public filtredExercices: ExerciceRow[] = [];
  public availableExerciceCategories: ExerciceCategory[] = [];

  private filterChange$ = new Subject<void>();
  public selectedCategoryIds: number[] = [];

  public selectedSort: exerciceOrderByOptions = exerciceOrderByOptions.created_desc;

  public haveToDuplicate = false;
  public haveToDuplicateAsModel = false;
  public modelId = 0;

  public readonly exercicesPerPage = 10;

  public sessionTitleFormControl: FormControl = new FormControl('', [
    Validators.required
  ]);
  public tagsFormControl = new FormControl('', Validators.required);

  public languagesFormControl: FormControl = new FormControl<number[]>([], [
    (ctrl) => ctrl.value && ctrl.value.length > 0 ? null : { required: true }
  ]);

  public get isLangSelectable(): boolean {
    return this.sessionRow.type !== Session.SESSION_TYPE_PERSONALIZED
  }

  @ViewChildren(SessionsGeneralPanelComponent) public generalPanels: QueryList<SessionsGeneralPanelComponent> = {} as QueryList<SessionsGeneralPanelComponent>;

  @ViewChild('editorComponentRef', {
    static: false,
    read: ViewContainerRef
  })
  public viewContainerRef: ViewContainerRef = {} as ViewContainerRef;

  @ViewChildren('stickyBoxRef') public stickyBox!: QueryList<ElementRef>;
  public sticky = false;
  public elementPosition: number | undefined;
  private unsubscribe$ = new Subject<void>();
  public selectedLanguages: number[] = [];
  public availableLangs: Lang[] = [];

  public dataSource: SimpleMatDataSource = new SimpleMatDataSource([]);
  public exerciseDatasource: ExerciseDatasource = {} as ExerciseDatasource;
  public exerciseRows$: Observable<ExerciceRow[]> | undefined;

  private _allExerciceRows: ExerciceRow[] = [];
  private _whenCloseBacktoUrl: string | undefined = undefined;
  private _fromLibraryId = 0;
  private _session: Session = {} as Session;
  private subscriptions: Subscription[] = [];

  private isNewSession = false;

  constructor(
    @Inject(ComponentFactoryResolver) public factoryResolver : ComponentFactoryResolver,
    public conf: ConfigProvider,
    public breadcrumbProvider: BreadcrumbProvider,
    public currentUser: CurrentUser,
    private _utils: UtilsProvider,
    private _title: Title,
    private _router: Router,
    private _activatedRouter: ActivatedRoute,
    private _toaster: MatSnackBar,
    private _currentUserRoles: CurrentUserRolesProvider,
    private _route: ActivatedRoute,
    private _translater: TranslateService,
    private _dialog: MatDialog,
    private api: ApiBaseService,
    private _sessionsProvider: SessionsProvider,
    private _sessionItemsProvider: SessionItemsProvider,
    private _exercicesProvider: ExercicesProvider,
    private _changesGuard: HasPendingChangesGuard,
    private _currentUser: CurrentUser,
    public editorService: EditorService,
    private errorManagerService: ErrorManagerService,
    private libraryProvider: LibraryService,
    private changeDetectorRef: ChangeDetectorRef,
    private langsProvider: LangsProvider,
  ) {
    this.subscriptions.push(
      this._activatedRouter.queryParams.subscribe((params) => {

        this.haveToDuplicate = (params['duplicate'] === 'true');
        this.haveToDuplicateAsModel = (params['duplicateAsModel'] === 'true');
        this.modelId = parseInt(params['modelId'], 10);
        if (params['backto']) {

          /**
           * !! Source de hack : je nettoie le protocole du lien et ajoute
           * le / (root) du début par sécurité. Si un utilisateur de saisir
           * le paramètre de l'url à la main, ça va le stopper.
           */
          this._whenCloseBacktoUrl = this._utils.clearUrlProtocole(params['backto']);
          if (this._whenCloseBacktoUrl.indexOf('/') !== 0) {
            // commence toujours par un / pour rester dans le domaine d'origine
            this._whenCloseBacktoUrl = '/' + this._whenCloseBacktoUrl;
          }
        }
        if (params['libraryId']) {
          this._fromLibraryId = parseInt(params['libraryId'], 10);
        }
      })
    );
  }

  @HostListener('window:beforeunload')
  public doSomething() {
    return !this._changesGuard.hasChanges();
  }

  public updateScrollPos(event: any) {
    this.sticky = Number(event.pos) >= Number(this.elementPosition) + 220;
  }

  public ngOnInit(): void {
    this._route.data.subscribe((data: any) => { //{ organization: Organization; library: Library; librariesFromOrganization: Library[]; session: Session }
      this.organization = data.organization;
      this.library = data.library;
      this.libraries = data.libraries;
      this._session = data.session;
      this.libraryContext = data.context;

      if (data.librariesFromOrganization) {
        this.librariesIds = this._utils.getKeyValues(data.librariesFromOrganization);
      } else {
        this.librariesIds = [this.library.id!];
      }

      this.sessionId = this._session?.id ?? 0;
      this.isNewSession = this.sessionId === 0;

      if (this._session.type && this._session.type === Session.SESSION_TYPE_PERSONALIZED) {
        this.tagsFormControl.removeValidators(Validators.required);
      }

      this.selectedLanguages = !this.haveToDuplicate && this._session?.availableLangIds || [];

      this.rights.evaluateScreenRights({
        rolesIds: this._currentUserRoles.getRolesIds(this.organization.id!)
      });

      if (!this.rights.ADD && !this.rights.EDIT) {
        this.is401 = true;
        return;
      }

      this.initPage().subscribe({
        next: () => {
          this.isLoading = false;
          this.changeDetectorRef.detectChanges();
        },
        error: (error: any) => {
          this.isLoading = false;
          this.errorManagerService.error(error);
          this.changeDetectorRef.detectChanges();
          this.onError(error);
        }
      });
    });

    this.subscriptions.push(
      this.editorService.blockClicked.subscribe((data:any) => {
        this.loadEditorComponent(data[1]);
      })
    );

    this.exerciseDatasource = new ExerciseDatasource(this._exercicesProvider);
    this.exerciseRows$ = this.dataSource.connect();

    this.loadExercisePaginated();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public ngAfterViewInit() {
    this.stickyBox.changes.pipe(
      startWith(this.stickyBox),
      takeUntil(this.unsubscribe$)
    ).subscribe((elements: QueryList<ElementRef>) => {
      if (elements.first) {
        this.elementPosition = elements.first.nativeElement.offsetTop;
      }
    });

    fromEvent<KeyboardEvent>(this.input?.nativeElement, 'keyup').pipe(
      debounceTime(300),
      filter((event: KeyboardEvent) => event.key === 'Enter' || event.keyCode === 13),
      distinctUntilChanged(),
      takeUntil(this.unsubscribe$),
    ).subscribe({
      next: () => {
        this.filterChange$.next();
      }
    });

    // trigger au click sur les filtres
    this.filterChange$.pipe(
      debounceTime(150),
      takeUntil(this.unsubscribe$),
    ).subscribe(() => {
      this.triggerSearch();
    });

    this.isLoading = false;
    this.updateExerciceDropList();

    this.paginator?.page.subscribe(() => this.loadExercisePaginated());

    const langId = this.langsProvider.getBestLangId();

    if (!this.selectedLanguages.includes(langId)) {
      this.selectedLanguages.push(langId);
    }

    this.languagesFormControl.setValue(this.selectedLanguages);

    this.availableLangs = this.langsProvider.getLangs();
    this.changeDetectorRef.detectChanges();
  }

  public ngAfterViewChecked() {
    if (this.needsDropListUpdate) {
      this.needsDropListUpdate = false;
      this.updateExerciceDropList();
    }
  }

  public toggleLanguage(langId: number, checked: boolean): void {
    if (checked) {
      this.selectedLanguages.push(langId);
    } else {
      const index = this.selectedLanguages.indexOf(langId);
      this.selectedLanguages.splice(index, 1);
    }

    this.languagesFormControl.setValue(this.selectedLanguages);
  }

  public isLangSelected(langId: number): boolean {
    return this.selectedLanguages.includes(langId);
  }

  protected triggerSearch() {
    this.paginator?.firstPage();
    this.loadExercisePaginated();
  }

  public onRefreshClicked(): void {
    this.isRefreshLoading = true;
    this.initPage().subscribe({
      next: () => {
        this.isRefreshLoading = false;
        this._toaster.open(this._translater.instant('Data_synchronized'), '', {duration: 5000});
      },
      error: (error: any) => {
        this.isRefreshLoading = false;
        this.errorManagerService.error(error);
        this.onError(error);
      }
    });
  }

  protected onSortSelection(sort: exerciceOrderByOptions): void {
    // Émettre un changement de filtre
    this.selectedSort = sort;
    console.log('Sort selection', sort);

    this.filterChange$.next();
  }

  protected onCategoryClicked(categoryId: number|undefined): void {
    // si pas de category id => on affiche tout
    if (!categoryId) {
      this.selectedCategoryIds = [];
    } else {
      // sinon on ajoute ou retire la catégorie
      if (this.selectedCategoryIds.includes(categoryId)) {
        this.selectedCategoryIds = this.selectedCategoryIds.filter((id) => id !== categoryId);
      } else {
        this.selectedCategoryIds.push(categoryId);
      }
    }
    // Émettre un changement de filtre
    this.filterChange$.next();
  }

  public onDuplicateSessionItem(elem: SessionItemRow): void {

    const duplicate = this._sessionItemsProvider.duplicateSessionItem(elem);
    this.sessionItems.splice((duplicate.ord??0) + 1, 0, duplicate);

    this.setOrder();
    this.updateResumedInfo();
  }

  public onMoveUpSessionItem(elem: SessionItemRow): void {

    let fromIndex = 0;
    let toIndex = 0;
    let family: any[];

    // détermine les index de la liste à switcher
    if (elem.parentIndex !== undefined && elem.parentIndex > -1) {

      // @ts-ignore
      family = this.sessionItems[elem.parentIndex].innerElements;
      fromIndex = elem.ord!;
      toIndex = (fromIndex - 1);
    } else {

      family = this.sessionItems;
      fromIndex = elem.ord!;
      toIndex = (fromIndex - 1);
    }

    // contrôle si c'est le premier...
    if (fromIndex === 0) {

      // impossible de monter le premier de la liste
      return;
    }

    // @ts-ignore
    family = this._utils.switchArrayItems(family, fromIndex, toIndex);
    if (family) {
      this.setOrder();
      this.updateResumedInfo();
      this.onFormChanged();
    }
  }

  public onMoveDownSessionItem(elem: SessionItemRow): void {

    let fromIndex = 0;
    let toIndex = 0;
    let arrayLength = -1;
    let family: any[];

    // détermine les index de la liste à switcher
    if (elem.parentIndex !== undefined && elem.parentIndex > -1) {

      // @ts-ignore
      family = this.sessionItems[elem.parentIndex].innerElements;
      // @ts-ignore
      arrayLength = family.length;
      fromIndex = elem.ord!;
      toIndex = (fromIndex + 1);
    } else {

      family = this.sessionItems;
      arrayLength = family.length;
      fromIndex = elem.ord!;
      toIndex = (fromIndex + 1);
    }

    // contrôle si c'est le dernier...
    if (fromIndex >= (arrayLength - 1)) {

      // impossible de descendre le dernier élément de la liste
      return;
    }

    // @ts-ignore
    family = this._utils.switchArrayItems(family, fromIndex, toIndex);
    if (family) {
      this.setOrder();
      this.updateResumedInfo();
      this.onFormChanged();
    }
  }

  public addNewBlocAtTheEnd(): void {
    this.sessionItems.push(this.getNewBloc());

    this.setOrder();
    this.updateResumedInfo();

    // force update last item of sessionItems
    this.changeDetectorRef.detectChanges();

    // Trigger update on next view check
    this.needsDropListUpdate = true;
  }

  public onPersonalizedExerciceClicked(sessionItemRow: SessionItemRow): void {
    this.isLoading = true;

    this._exercicesProvider.getExerciceRow(this.organization!, sessionItemRow.data.sessionItemExercice.exerciceId).pipe(
      map((exerciceRow) => {
        exerciceRow.exercice.id = 0;
        exerciceRow.exercice.type = Exercice.TYPE_PERSONALIZED;
        exerciceRow.exercice.libraryId = undefined;
        exerciceRow.exercice.modelId = undefined;

        const dialogRef: any = this._dialog.open(ExercicesFormComponent, {
          data: {
            organization: this.organization,
            availableExerciceCategories: this.availableExerciceCategories,
            exerciseRow: exerciceRow,
            formTitle: 'Exercise_personalization',
            confirmButtonTitle: 'Personalize',
            exerciceLibraryId: this.library ? this.library.id : null,
            saveInfo: this._translater.instant('Personalize_exercice_info_alt1'),
            disableDuration: true,
            hideDuration: true,
            isLangSelectable: false
          }
        });

        this.isLoading = false;

        this.subscriptions.push(
          dialogRef.afterClosed().subscribe((row: ExerciceRow) => {
            if (row) {
              forkJoin([
                this._exercicesProvider.getImageFile(this.organization.id!, row),
                this._exercicesProvider.getVideoFile(this.organization.id!, row)
              ]).pipe(
                map((media) => {
                  row.exerciceImage = media[0];
                  row.exerciceVideo = media[1];
                  sessionItemRow.data.exercice = row;
                  sessionItemRow.data.sessionItemExercice.exerciceId = row.exercice.id;

                  this.updateResumedInfo();
                  this.onFormChanged();
                })
              ).subscribe();
            }
          })
        );
      })
    ).subscribe();
  }

  public toggleLeftPanel(): void {
    this.isLeftPanelOpen = !this.isLeftPanelOpen;
  }

  public onTotalTimeChange(event: EventEmitter<any>): void {
    this.sessionRow.totalTime = this.generalPanels.first._totalTime;
    // this.updateResumedInfo();
    // this.generalPanels.first.updateResumedInfo();
  }

  public onIntensitySliderChange(event: any): void {
    this.sessionRow.intensity = event.value;
    this.onFormChanged();
  }

  public onDeleteSessionItem(toDelete: SessionItemRow): void {
    this.deleteSessionItemRow(toDelete);
    this.setOrder();
    this.updateResumedInfo();
    this.onFormChanged();
  }

  public getNewBloc(): SessionItemRow {
    return this._sessionItemsProvider.getEmptyItem(SessionItemsProvider.TYPE_BLOC);

  }

  public getNewComment(): SessionItemRow {
    return this._sessionItemsProvider.getEmptyItem(SessionItemsProvider.TYPE_COMMENT);
  }

  public getNewRest(): SessionItemRow {
    return this._sessionItemsProvider.getEmptyItem(SessionItemsProvider.TYPE_REST);
  }

  public getNewExercice(exercice: ExerciceRow): SessionItemRow {

    const toReturn: SessionItemRow = this._sessionItemsProvider.getEmptyItemExercice(exercice);

    return toReturn;

  }

  public onDragGeneralItem(event: EventEmitter<any>, type: string): void {

    this.isLeftPanelOpen = false;

  }

  noReturnPredicate() {
    return false;
  }

  rootPredicate(drag: CdkDrag<any>, drop: CdkDropList<any>) {
    return drag.data.type === SessionItemsProvider.TYPE_BLOC || drag.data.type === SessionItemsProvider.TYPE_COMMENT || drag.data.type === SessionItemsProvider.TYPE_REST;
  }

  blocPredicate(drag: CdkDrag<any>, drop: CdkDropList<any>) {
    return drag.data.type === SessionItemsProvider.TYPE_EXERCICE || drag.data.type === SessionItemsProvider.TYPE_COMMENT || drag.data.type === SessionItemsProvider.TYPE_REST;
  }

  public onDropRoot(event: CdkDragDrop<SessionItemRow[]>): void {
    // get item and add it to SessionItems
    const droppedItem = event.item.data;

    // if not type bloc, comment or rest, do nothing : no exercice on root
    if (droppedItem.type === SessionItemsProvider.TYPE_BLOC || droppedItem.type === SessionItemsProvider.TYPE_COMMENT || droppedItem.type === SessionItemsProvider.TYPE_REST) {
      // if from other container, add to sessionItems and udpate order from session items
      if (event.previousContainer !== event.container) {
        event.previousIndex = this.sessionItems.length;
        this.sessionItems.push(droppedItem);
      }
      // reorder session items
      moveItemInArray(this.sessionItems, event.previousIndex, event.currentIndex);
    }
    this.onFormChanged();
    this.setOrder();
    this.updateResumedInfo();

    // Update drop zones after DOM changes
    this.changeDetectorRef.detectChanges();
    // Trigger update on next view check
    this.needsDropListUpdate = true;
  }

  public updateExerciceDropList(): void {
    // find all droplist in element
    const dropListParent = document.getElementById('root-drop-list');
    if (dropListParent) {
      // Initial scan
      const scanDropLists = () => {
        let dropList: string[] = [];
        dropListParent?.querySelectorAll('.cdk-drop-list').forEach(el => {
          if (el.id) { // Only add if ID exists
            dropList.push(el.id);
          }
        });
        this.exerciceDropList = dropList;
        // Force change detection to update bindings
        this.changeDetectorRef.markForCheck();
      };

      // Scan immediately
      scanDropLists();

      // Setup observer for future changes
      const observer = new MutationObserver(() => {
        scanDropLists();
      });
      observer.observe(dropListParent, { childList: true, subtree: true });
    } else {
      console.error('Element with id "root-drop-list" not found.');
    }
  }

  public onDropChild(event: CdkDragDrop<SessionItemRow[]>, parentItem: SessionItemRow): void {
    // get item and add it to parentItem
    const droppedItem = event?.item.data;
    if (!droppedItem) {
      return;
    }

    if (parentItem.type === SessionItemsProvider.TYPE_BLOC && (droppedItem.type === SessionItemsProvider.TYPE_EXERCICE || droppedItem.type === SessionItemsProvider.TYPE_COMMENT || droppedItem.type === SessionItemsProvider.TYPE_REST)) {
      // if from other container, add to children and udpate order
      if (event.previousContainer !== event.container) {
        // if was in root, remove from root
        if ((event.previousContainer.id != 'main-drop-items') && (event.previousContainer.id != 'exercice-drop-items')) {
          this.onDeleteSessionItem(droppedItem);
        }

        event.previousIndex = parentItem.innerElements.length;
        parentItem.innerElements.push(droppedItem);

      }

      moveItemInArray(parentItem.innerElements, event.previousIndex, event.currentIndex);
    }
    // SD Dirty fix, but working one
    this.loadingResumedInfo = true;
    this.disableComputedInput();
    this.setOrder();

    setTimeout(() => {

      this.loadingResumedInfo = false;
      this.enableComputedInput();
      this.updateResumedInfo();
      this.onFormChanged();

    }, 500);
  }

  public onSaveClicked(event: any): void {
    this.allAsTouched();

    if (!this.isFormValid()) {
      this._toaster.open(this._translater.instant('Correct_form_errors'), '', { duration: 5000 });
      return;
    }

    // detect wrong exercice configuration
    const result = this.findInvalidItem();
    if (result.item) {
      if (result.item instanceof Exercice) {
        this._toaster.open(this._translater.instant('Exercice_not_configured', {x: result.item.name, n: result.counter.exercice}), '', {duration: 5000});
      } else if (isObject(result.item)) {
        if (result.item.type === SessionItem.TYPE_REST) {
          this._toaster.open(this._translater.instant('Rest_not_configured', {n: result.counter.rest}), '', {duration: 5000});
        } else if (result.item.type === SessionItemWrapper.TYPE_BLOC) {
          this._toaster.open(this._translater.instant('Bloc_not_configured', {n: result.counter.block}), '', {duration: 5000});
        } else {
          if (result.item.name) {
            this._toaster.open(this._translater.instant('Exercice_not_configured', {x: result.item.name}), '', {duration: 5000});
          } else {
            this._toaster.open(this._translater.instant('Session_save_error'), '', {duration: 5000});
          }
        }
      }

      return;
    } else {
      this.isLoading = true;

      this.save().subscribe({
        next: () => {
          this.isLoading = false;
          this._toaster.open(this._translater.instant('Session_saved'), '', {duration: 5000});
          this._changesGuard.unregisterChanges();
          // remove url params duplicate or duplicateAsModel and refresh page
          if (this.isNewSession || this.haveToDuplicate || this.haveToDuplicateAsModel) {
            this.isNewSession = false;
            this.haveToDuplicate = false;
            this.haveToDuplicateAsModel = false;
            // Récupérer les queryParams existants
            const currentQueryParams = this._activatedRouter.snapshot.queryParams;

            // Detect the current route context to build the correct URL
            const currentPath = this._route.snapshot.url.map(segment => segment.path).join('/');
            let url = `/${this.organization.code}/`;

            if (this.library.id) {
              url += `libraries/${this.library.id}/`;
            }

            // Build context-specific URL
            if (currentPath.includes('sessions-elitment')) {
              url += `sessions-elitment/${this.sessionId}/editor`;
            } else if (currentPath.includes('sessions-super-coach')) {
              url += `sessions-super-coach/${this.sessionId}/editor`;
            } else {
              // Default to personal sessions
              url += `sessions/personal/${this.sessionId}/editor`;
            }

            this._router.navigate(
              [url],
              { queryParams: { ...currentQueryParams, duplicate: null, duplicateAsModel: null, modelId: null } }
            );
          } else {
            this.onRefreshClicked();
          }

          return true;
        },
        error: (response: any) => {
          this.isLoading = false;
          if (response.status === 422) {
            response = "no_exercices_in_session";
          }
          this.errorManagerService.error(response);
          this.onError(response);
          throw response;
        },
      });
    }
  }

  public onCancelClicked(): void {
    if (this._whenCloseBacktoUrl === undefined) {

      // Detect the current route context to navigate back correctly
      const currentPath = this._route.snapshot.url.map(segment => segment.path).join('/');
      const route: string[] = [this.organization!.code!];

      if (this.library) {
        route.push('libraries');
        route.push('' + this.library.id);
      }

      // Determine the correct session route based on context
      if (currentPath.includes('sessions-elitment')) {
        route.push('sessions-elitment');
      } else if (currentPath.includes('sessions-super-coach')) {
        route.push('sessions-super-coach');
      } else {
        // Default to personal sessions
        route.push('sessions');
        route.push('personal');
      }

      // TODO
      // si on a créé une nouvelle session ou qu'on a l'a dupliquée
      // on ne connait pas son identifiant avant d'enregistrer ...
      // et potentiellement le user l'enregistrera après qu'on essayer de naviguer
      // idéalement il faudrait pouvoir détecter s'il a décidé d'enregistrer ou non ses modifications
      // si oui, on redirige vers la session qui vient d'être créée
      // si non, on redirige vers la liste d'où il vient
      if (this.sessionRow.id && !(currentPath.includes('sessions-elitment') || currentPath.includes('sessions-super-coach') || currentPath.includes('sessions-personal'))) {
        route.push('' + this.sessionRow.id);
      }

      this._router.navigate(route);
    } else {

      // j'ai une url pour le retour
      this._router.navigateByUrl(this._whenCloseBacktoUrl);
    }
  }

  public getBlocNumber(sessionItemRow: SessionItemRow): number {

    const allBlocs = this._sessionItemsProvider.getSessionItemFromSessionItemRows(this.sessionItems, [SessionItemsProvider.TYPE_BLOC]);
    let toReturn = 1;

    allBlocs.forEach((bloc, index) => {

      if (bloc === sessionItemRow) {
        toReturn += index;
        return;
      }

    });

    return toReturn;

  }

  public onGlobalCommentChange(event: EventEmitter<any>): void {
    this.onFormChanged();
  }

  public onExerciceListPageChanged(): void {
    // this.loadExerciceThumbnails();
  }

  public onFormChanged(): void {
    this._changesGuard.registerChanges(() => this.onSaveClicked(null));
  }

  public changeAllValuesAreGood(good: boolean) {
    this.allValuesAreGood = good;
  }

  public onDuplicateExerciceAsModel(exToDuplicate: SessionItemRow) {
    this.isLoading = true;
    this._exercicesProvider.getExerciceRow(this.organization, exToDuplicate.data.exercice.exercice.id, true, true, false).subscribe({
      next: (exRow: ExerciceRow) => {
        forkJoin([
          this._exercicesProvider.getImageFile(this.organization.id!, exRow, 'file'),
          this._exercicesProvider.getVideoFile(this.organization.id!, exRow, 'file'),
          this._exercicesProvider.getImageFile(this.organization.id!, exRow),
          this.libraryProvider.getByOrganizationId(this.organization.id!),
        ]).pipe(
          map((data: any[]) => {

            const exerciceRow: ExerciceRow = {
              exercice: exRow.exercice,
              categories: exRow.categories.slice(),
              exerciceImage: data[2],
              exerciceVideo: data[1],
              creater: this.currentUser.user,
            };

            exerciceRow.exercice.id = 0;
            exerciceRow.exercice.name = exerciceRow.exercice.name + ' (1)';
            exerciceRow.exercice.type = Exercice.TYPE_MODEL;
            exerciceRow.exercice.libraryId = data[3].id;
            exerciceRow.exercice.image = data[0];
            exerciceRow.exercice.video = data[1];
            // exerciceRow.exercice.video_path = data[1];

            const dialogRef: any = this._dialog.open(ExercicesFormComponent, {
              data: {
                organization: this.organization,
                availableExerciceCategories: this.availableExerciceCategories,
                exerciseRow: exerciceRow,
                formTitle: 'Make_a_model',
                confirmButtonTitle: 'Save',
                skipLangSelection: true,
                exerciceLibraryId: data[3].id
              }
            });

            this.subscriptions.push(
              dialogRef.afterClosed().subscribe((row: ExerciceRow) => {
                if (row) {
                  Promise.all([
                    this._exercicesProvider.getImageFile(this.organization.id!, row)
                  ]).then((media) => {

                    row.exerciceImage = media[0];
                    this.filtredExercices.push(row);

                    this.isLoading = false;

                    this._toaster.open(this._translater.instant('Exercice_saved_as_model'), '', {duration: 5000});
                  });
                }
              })
            );
          })
        ).subscribe();
      },
      error: (error: any) => {
        this.isLoading = false;
        this.errorManagerService.error(error);
      },
    });
  }

  private findInvalidItem() {

    const counter = {
      exercice: 0,
      block: 0,
      rest: 0,
    };

    if (this.sessionItems.length > 0) {
      for (let i = 0; i < this.sessionItems.length; i++) {

        const block = this.sessionItems[i];
        if (block.type === SessionItemWrapper.TYPE_BLOC) {
          counter.block++;
          if (block.data.nbRepeat < 1) {
            return {
              item: block,
              counter,
            };
          }
        }
        // if (block.type === SessionItemWrapper.TYPE_REST) {
        //     counter.rest++;
        //     if (block.data.restTime < 1) {
        //         return {
        //             item: block,
        //             counter: counter
        //         };
        //     }
        // }

        for (let j = 0; j < block.innerElements.length; j++) {

          const element = block.innerElements[j];

          // exercice
          if (element.type === SessionItemExercice.TYPE_EXERCICE) {

            counter.exercice++;
            const exercice = element.data.exercice.exercice;
            const sessionExercice = element.data.sessionItemExercice;
            if (exercice.exerciceType === Exercice.TYPE_REPEAT) {

              // nbSerie, nbRepeat and duration must be qualified
              // if (sessionExercice.nbRepeat < 1 || sessionExercice.nbSerie < 1 || sessionExercice.duration < 1) {
              if (sessionExercice.nbRepeat < 1 || sessionExercice.nbSerie < 1) {
                return {
                  item: exercice,
                  counter
                };
              }
            } else if (exercice.exerciceType === Exercice.TYPE_DURATION) {

              // nbRepeat and duration must ne qualified
              // if (sessionExercice.nbRepeat < 1 || sessionExercice.duration < 1) {
              if (sessionExercice.nbRepeat < 1) {
                return {
                  item: exercice,
                  counter
                };
              }
            }
          }

          // rest
          // if (element.type === SessionItem.TYPE_REST) {
          //     counter.rest++;
          //     if (element.data.restTime < 1) {
          //         return {
          //             item: element,
          //             counter: counter
          //         };
          //     }
          // }
        }
      }
    }

    // not found
    return {
      item: null,
      counter,
    };
  }

  private deleteSessionItemRow(toDelete: SessionItemRow): void {
    if (this.sessionItems.indexOf(toDelete) > -1) {
      this.sessionItems.splice(this.sessionItems.indexOf(toDelete), 1);
    } else {
      this.sessionItems.forEach((sessionItem) => {
        if (sessionItem.innerElements.indexOf(toDelete) < 0) {
          return;
        }

        sessionItem.innerElements.splice(sessionItem.innerElements.indexOf(toDelete), 1);
      });
    }
  }

  private loadExerciceThumbnails(index: number | null = null): void {
    /* if (!this.paginators.length) {
        return;
    }

    if (index === null) {
        index = this.paginators.first.pageIndex;
    }

    const from: number = index * this.paginators.first.pageSize;
    const to: number = from + this.paginators.first.pageSize;
    for (let i = from; i < to && i < this.filtredExercices.length; i++) {
        const row: ExerciceRow = this.filtredExercices[i];
        if (!row.exerciceImage) { // si elle est d�j� load�e, on la reload pas ...
            this._exercicesProvider.getImageFile(this.organization.id, row)
                .then((image: any) => row.exerciceImage = image)
                .catch((error: any) => console.log('Failed to load thumbnail', error));
        }
    }*/
  }

  private setOrder(): void {
    this.sessionItems.forEach((sessionItem, index) => {

      sessionItem.ord = index;

      sessionItem.innerElements.forEach((innerElement, innerIndex) => {
        innerElement.ord = innerIndex;
      });

    });
  }

  private save(): Observable<void> {
    try {
      this.retrieveFormControl();
      this.setOrder();

      return this._sessionItemsProvider.saveSessionItemRows(this.organization.id!, this.sessionId, this.sessionItems, this.sessionRow, this.isLangSelectable ? this.selectedLanguages : undefined).pipe(
        switchMap((session) => {
          this.sessionId = session.id ?? 0;
          return this._sessionsProvider.getSession({id: this.sessionId, filled: true})
            .pipe(
              switchMap((session: Session) => {
                this._session = session;
                return this._sessionItemsProvider.buildSessionItemRowsFromSession(this.organization, this._session).pipe(
                  map((sessionItems: SessionItemRow[]) => {
                    this.sessionRow = this._sessionsProvider.createSessionRow(this.organization, this._session, this.library.id);
                    this.sessionItems = sessionItems;
                  }),
                  catchError((error: any) => {
                    return throwError(() => error);
                  }),
                );
              }),
              catchError((error: any) => {
                return throwError(() => error);
              }),
            );
        }),
        catchError((error: any) => {
          return throwError(() => error);
        }),
      );
    } catch (error) {
      return throwError(() => error);
    }
  }

  private initPage(): Observable<any> {
    return this.loadData().pipe(
      map(() => {
        // TODO : Fix THAT hacky code
        // hack pour laisser le temps au ViewChild de r�cup�rer la vue ...
        setTimeout(() => {
          this.initNav();
          this.setFormControl();
          this.isLoading = false;
          /* this.subscriptions.push(
              this.paginators.changes.subscribe(() => this.loadExerciceThumbnails()) // pour initialiser la 1�re page d'exercices
          );*/
          this.generalPanels.first.updateResumedInfo();
          this.generalPanels.first._totalTime = this.sessionRow.totalTime!;
          this.viewContainerRef.clear();
          this._changesGuard.unregisterChanges();
        }, 1);
      }),
    );
  }

  private initNav(): void {
    if (!this.rights.ADD && !this.rights.EDIT) {
      this.is401 = true;
      return;
    }

    this.createPageTitle();
    this.createBreadcrumb();
  }

  private loadData(): Observable<any> {
    const sessionToLoad = this.sessionId || this.modelId;

    if (isNaN(sessionToLoad) || sessionToLoad === 0) {
      this._session = {} as Session;
      // default values
      this.sessionRow.libraryId = this.library.id;
      this.sessionRow.type = Session.SESSION_TYPE_MODEL;
      this.sessionRow.intensity = 0;
      this.sessionRow.comment = '';
      return of(null);
    }

    let getSessionObserver$ = of(this._session)
    if (!this._session.id) {
      getSessionObserver$ = this._sessionsProvider.getSession({id: sessionToLoad, filled: true});
    }
    return getSessionObserver$.pipe(
      switchMap((session: Session) => {
        this._session = session;
        return forkJoin([
          this._sessionsProvider.getSessionRow({organization: this.organization!, session: this._session, filled: true}),
          this._sessionItemsProvider.buildSessionItemRowsFromSession(this.organization, this._session),
        ]).pipe(
          switchMap(([sessionRow, sessionItems] : [SessionRow, SessionItemRow[]]) => {
            this.sessionRow = sessionRow;
            this.sessionItems = sessionItems;

            if (this.haveToDuplicate || this.haveToDuplicateAsModel) {
              this.sessionRow.id = 0;
              this._session.id = 0;
              this.sessionId = 0;
            }
            if (this.haveToDuplicateAsModel) {
              this.sessionRow.type = Session.SESSION_TYPE_MODEL;
              this.sessionRow.name = this.sessionRow.name + ' (1)';
              this.sessionRow.libraryId = this._fromLibraryId;
            }
            this.rights.evaluateScreenRights({
              rolesIds: this._currentUserRoles.getRolesIds(this.organization.id!)
            });

            return forkJoin([
              this._exercicesProvider.getExerciceRows(this.organization, this._utils.getKeyValues(this.libraries), false, false, false),
              this._exercicesProvider.getExerciceCategories(),
            ]).pipe(
              map(([exerciceRows, availableExerciceCategories] : [Paginator, ExerciceCategory[]]) => {
                this._allExerciceRows = exerciceRows.data as ExerciceRow[];
                this.availableExerciceCategories = availableExerciceCategories;
              }),
              catchError((error: any) => {
                this.isLoading = false;
                this.errorManagerService.error(error);
                this.onError(error);
                return of(null);
              }),
            );
          }),
          catchError((error: any) => {
            this.isLoading = false;
            this.errorManagerService.error(error);
            this.onError(error);
            return of(null);
          }),
        );
      }),
    );
  }

  protected updateResumedInfo(): void {
    this.onFormChanged();
    this.retrieveFormControl();
    this.generalPanels.first.updateResumedInfo();
  }

  private createPageTitle() {
    this.pageTitle = this._translater.instant('Session_editor') + (this.sessionRow ? ' ' + this.sessionRow.name : '');
    this._title.setTitle(this.pageTitle + ' | ' + this.conf.platformName);
  }

  private createBreadcrumb() {

    /**
     * Multiple cas de figure pour le fil d'arianne :
     * 1) Si on modifie un modèle de séance ou une de mes séance
     * 2) Si on personnalise une séance depuis le calendrier d'un athlète
     * 3) Si on personnalise une séance depuis le dashboard d'un athlète
     */
    const crumbs: Crumb[] = [];
    if (!this.sessionRow.libraryId && this.sessionRow.type !== Session.SESSION_TYPE_PERSONALIZED) {
      crumbs.push({
        shortTitle: this.sessionRow.name ?? '',
        title: this.sessionRow.name ?? '',
        url: this.organization!.code + '/sessions/' + this.sessionRow.id,
      });
      crumbs.push(
        {
          shortTitle: 'Edition',
          title: 'Edition',
          url: this.organization!.code + '/sessions/' + this.sessionRow.id + '/editor'
        }
      );
      console.log("crumbs", crumbs)
      this.breadcrumbProvider.setDefaultCrumbs(crumbs);
      return;
    }

    this.libraryProvider.get(this.organization?.code!, this.sessionRow.libraryId! || this.library.id!).subscribe({
      next: (library: Library) => {
        if (this._whenCloseBacktoUrl && this._whenCloseBacktoUrl.indexOf('calendar') > -1) {

          // depuis le calendrier d'un athlète
          // this._whenCloseBacktoUrl = /elitment/athletes/14/calendar/14/2019-10-11
          crumbs.push({
            shortTitle: this._translater.instant('Calendar'),
            title: this._translater.instant('Calendar'),
            url: this._whenCloseBacktoUrl,
          });
          if (this.sessionRow.name) {
            crumbs.push({
              shortTitle: this.sessionRow.name,
              title: this.sessionRow.name,
              url: this._whenCloseBacktoUrl,
            });
          }
          // Ajouter le crumb "Modification"
          crumbs.push({
            shortTitle: 'Edition',
            title: 'Edition',
          });
        } else if (this._whenCloseBacktoUrl && this._whenCloseBacktoUrl.indexOf('athletes') > -1) {

          // depuis le dashboard d'un athlète
          // this._whenCloseBacktoUrl = /elitment/athletes/14
          crumbs.push({
            shortTitle: this._translater.instant('Athlete_Dashboard'),
            title: this._translater.instant('Athlete_Dashboard'),
            url: this._whenCloseBacktoUrl,
          });
          if (this.sessionRow.name) {
            crumbs.push({
              shortTitle: this.sessionRow.name,
              title: this.sessionRow.name,
              url: this._whenCloseBacktoUrl,
            });
          }
        } else {
          this.subscriptions.push(
            this._route.data.subscribe((data:any) => {
              // Detect the current route context
              const currentPath = this._route.snapshot.url.map(segment => segment.path).join('/');
              const libraryId = this.sessionRow.libraryId || library.id;
              let listUrl: string;
              let detailUrl: string;
              let editorUrl: string;
              let categoryTitle: string;

              if (currentPath.includes('sessions-elitment')) {
                // Elitment sessions context
                categoryTitle = this._translater.instant('Elitment_sessions_title');
                listUrl = this.organization!.code + '/libraries/' + libraryId + '/sessions-elitment';
                detailUrl = this.organization!.code + '/libraries/' + libraryId + '/sessions-elitment/' + this.sessionRow.id;
                editorUrl = this.organization!.code + '/libraries/' + libraryId + '/sessions-elitment/' + this.sessionRow.id + '/editor';
                crumbs.push({
                  shortTitle: categoryTitle,
                  title: categoryTitle,
                  url: listUrl,
                });
              } else if (currentPath.includes('sessions-super-coach')) {
                // Super Coach sessions context
                categoryTitle = this._translater.instant('Super_coaches_sessions_title');
                listUrl = this.organization!.code + '/libraries/' + libraryId + '/sessions-super-coach';
                detailUrl = this.organization!.code + '/libraries/' + libraryId + '/sessions-super-coach/' + this.sessionRow.id;
                editorUrl = this.organization!.code + '/libraries/' + libraryId + '/sessions-super-coach/' + this.sessionRow.id + '/editor';
                crumbs.push({
                  shortTitle: categoryTitle,
                  title: categoryTitle,
                  url: listUrl,
                });
              } else {
                // Personal sessions context (default for old routes)
                categoryTitle = this._translater.instant('Personal_sessions_title');
                listUrl = this.organization!.code + '/libraries/' + libraryId + '/sessions/personal';
                detailUrl = this.organization!.code + '/libraries/' + libraryId + '/sessions/personal/' + this.sessionRow.id;
                editorUrl = this.organization!.code + '/libraries/' + libraryId + '/sessions/personal/' + this.sessionRow.id + '/editor';
                crumbs.push({
                  shortTitle: categoryTitle,
                  title: categoryTitle,
                  url: listUrl,
                });
              }

              if (this.sessionRow.name) {
                crumbs.push({
                  shortTitle: this.sessionRow.name,
                  title: this.sessionRow.name,
                  url: detailUrl,
                });
              }

              crumbs.push(
                {
                  shortTitle: 'Edition',
                  title: 'Edition',
                  url: editorUrl
                }
              );
            })
          );
        }

        this.breadcrumbProvider.setDefaultCrumbs(crumbs);

      }
    });
  }

  private setFormControl(): void {
    this.sessionTitleFormControl.setValue(this.sessionRow.name);
    this.subscriptions.push(
      this.sessionTitleFormControl.valueChanges.subscribe(() => {
        this.onFormChanged();
      })
    );

    this.tagsFormControl.setValue(this.sessionRow.tags ?? null);
    this.subscriptions.push(
      this.tagsFormControl.valueChanges.subscribe(() => {
        this.onFormChanged();
      })
    );

    this.generalPanels.first.totalTimeFormControl.setValue(this.generalPanels.first._totalTime);
    this.subscriptions.push(
      this.generalPanels.first.totalTimeFormControl.valueChanges.subscribe(() => {
        // FG: je sais pas pourquoi le temps total est géré via un formControl ;
        // il s'agit d'un champ calculé à partir des autres inputs de l'éditeur
        // et l'utilisateur n'a jamais la possibilité de le changer...
        // sachant que cette subscription pose problème quand on enregistre
        // et en prenant en compte le commentaire ci-dessus, on enregistre pas de changement
        // dans le guard quand cette valeur est mise à jour...
        // this.onFormChanged();
      })
    );

    this.generalPanels.first.nbRepeatFormControl.setValue(this.sessionRow.nbRepeat);
    this.subscriptions.push(
      this.generalPanels.first.nbRepeatFormControl.valueChanges.subscribe(() => {
        this.onFormChanged();
      })
    );
  }

  private retrieveFormControl(): void {
    this.sessionRow.name = this.sessionTitleFormControl.value;
    this.sessionRow.tags = this.tagsFormControl.value ?? undefined;
    this.generalPanels.first._totalTime = this.generalPanels.first.totalTimeFormControl.value;
    this.sessionRow.totalTime = this.generalPanels.first._totalTime;
    this.sessionRow.nbRepeat = this.generalPanels.first.nbRepeatFormControl.value;
  }

  private allAsTouched(): void {
    this.sessionTitleFormControl.markAsTouched();
    this.tagsFormControl.markAsTouched();
    this.generalPanels.first.totalTimeFormControl.markAsTouched();
    this.generalPanels.first.nbRepeatFormControl.markAsTouched();
    this.languagesFormControl.markAsTouched();
  }

  private isFormValid(): boolean {
    return !this.sessionTitleFormControl.invalid
      && !this.tagsFormControl.invalid
      && !this.generalPanels.first.totalTimeFormControl.invalid
      && !this.generalPanels.first.nbRepeatFormControl.invalid
      && !this.languagesFormControl.invalid;
  }

  private disableComputedInput(): void {
    this.generalPanels.first.nbRepeatFormControl.disable();
    this.generalPanels.first.totalTimeFormControl.disable();
  }

  private enableComputedInput(): void {
    this.generalPanels.first.nbRepeatFormControl.enable();
    this.generalPanels.first.totalTimeFormControl.enable();
  }

  private onError(error: any): void {
    this.isLoading = false;

    if (error.status === 404) {
      this.is404 = true;
    } else if (error.status === 401) {
      this.is401 = true;
    }
  }

  private loadEditorComponent(componentToLoad: EditableBlockInterface) {
    const factory = this.factoryResolver.resolveComponentFactory(componentToLoad.editComponent());

    this.viewContainerRef.clear();

    const componentRef = this.viewContainerRef.createComponent(factory);
    const component = componentRef.instance as SessionBlocEditComponent;
    // component.data = componentToLoad.data;
    component.component = componentToLoad;

    // this.questionComponent = component;
  }

  private loadExercisePaginated() {
    this.sideLoading = true;
    const pageIndex = (this.paginator?.pageIndex ?? 0) + 1;
    const keyword = this.input?.nativeElement?.value;

    return this.exerciseDatasource.loadExerciseRows(
      this.organization,
      this.librariesIds,
      undefined,
      true,
      true,
      true,
      pageIndex,
      this.exercicesPerPage,
      keyword,
      this.selectedCategoryIds,
      this.selectedSort,
    ).subscribe({
      next: () => {
        this.sideLoading = false;
        this.updateTableData();
      },
      error: (error: any) => {
        this.sideLoading = false;
        this.errorManagerService.error(error);
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  private updateTableData(): void {
    this.dataSource.data = this.exerciseDatasource.subject.getValue();
    this.dataSource._updateChangeSubscription(); // Explicitly inform the table that the data has changed
    this.changeDetectorRef.detectChanges();
  }

}

// SD Sheit needed for dnd plugin, cause they need this kind of structure but they do not provide them...
// Waiting for update.

// @ts-ignore
declare module '@swimlane/dragula' {
  interface DragulaOptions {
    containers?: Element[];
    isContainer?: (el?: Element) => boolean;
    moves?: (el?: Element, container?: Element, handle?: Element, sibling?: Element) => boolean;
    accepts?: (el?: Element, target?: Element, source?: Element, sibling?: Element) => boolean;
    invalid?: (el?: Element, target?: Element) => boolean;
    direction?: string;
    copy?: ((el: Element, source: Element) => boolean) | boolean;
    revertOnSpill?: boolean;
    removeOnSpill?: boolean;
    delay?: boolean | number;
    mirrorContainer?: Element;
    ignoreInputTextSelection?: boolean;
  }

  interface Drake {
    containers: Element[];
    dragging: boolean;

    start(item: Element): void;

    end(): void;

    cancel(revert: boolean): void;

    cancel(): void;

    remove(): void;

    on(events: string, callback: Function): void;

    destroy(): void;
  }

  interface Dragula {
    (containers: Element[], options: DragulaOptions): Drake;

    (containers: Element, options: DragulaOptions): Drake;

    (containers: Element[]): Drake;

    (options: DragulaOptions): Drake;

    (): Drake;
  }
}
